import type { FloorInfo, FloorLayoutInfo, ManagedTableInfo } from '@/types/api';
import type { PendingTableOp, TablePosition } from '@/components/TableLayoutEditor';

interface ApplyPendingTableOpsDeps {
  restaurantId: number;
  floorId: number;
  deleteManagedTable: (restaurantId: number, tableId: number) => Promise<void>;
  detachManagedTable: (restaurantId: number, tableId: number) => Promise<void>;
  createManagedTable: (
    restaurantId: number,
    floorId: number,
    data: Partial<ManagedTableInfo>
  ) => Promise<ManagedTableInfo>;
}

/**
 * Commits the canvas popup's staged add/detach/delete actions in the order the design requires:
 * deletes -> detaches -> creates -> assigns. Deletes/detaches run first so a table added and then
 * immediately removed in the same session never gets created at all (its pending create/assign op
 * is dropped client-side in TableLayoutEditor, never reaching here); creates/assigns run last and
 * return a temp-id -> real-id map so the caller can fold the newly-created tables into the normal
 * position/shape save pass.
 */
export async function applyPendingTableOps(
  pendingOps: PendingTableOp[],
  positions: TablePosition[],
  deps: ApplyPendingTableOpsDeps
): Promise<{ tempIdToRealId: Map<number, number> }> {
  const tempIdToRealId = new Map<number, number>();

  for (const op of pendingOps) {
    if (op.type === 'delete') {
      await deps.deleteManagedTable(deps.restaurantId, op.tableId);
    }
  }
  for (const op of pendingOps) {
    if (op.type === 'detach') {
      await deps.detachManagedTable(deps.restaurantId, op.tableId);
    }
  }
  for (const op of pendingOps) {
    if (op.type === 'create') {
      const pos = positions.find((p) => p.tableId === op.tempId);
      const created = await deps.createManagedTable(deps.restaurantId, deps.floorId, {
        name: op.name,
        capacity: op.capacity,
        x: pos?.x,
        y: pos?.y,
        width: pos?.width,
        height: pos?.height,
        shape: pos?.shape,
      });
      tempIdToRealId.set(op.tempId, created.id);
    }
  }
  for (const op of pendingOps) {
    if (op.type === 'assign') {
      const pos = positions.find((p) => p.tableId === op.tempId);
      const created = await deps.createManagedTable(deps.restaurantId, deps.floorId, {
        name: op.sourceTable.tableNumber,
        capacity: op.sourceTable.capacity,
        posTableId: op.posTableId,
        x: pos?.x,
        y: pos?.y,
        width: pos?.width,
        height: pos?.height,
      });
      tempIdToRealId.set(op.tempId, created.id);
    }
  }

  return { tempIdToRealId };
}

/**
 * Produces the position list for the ordinary (pre-existing) position/shape save pass: real
 * tables that weren't deleted/detached this round, plus newly created/assigned tables remapped
 * from their temp id to the real id `applyPendingTableOps` returned. A temp id with no mapping
 * (its create/assign never ran, e.g. it wasn't part of this save) is dropped rather than sent
 * with a meaningless negative id.
 */
export function buildFinalPositions(
  positions: TablePosition[],
  pendingOps: PendingTableOp[],
  tempIdToRealId: Map<number, number>
): TablePosition[] {
  const removedIds = new Set(
    pendingOps
      .filter((op): op is Extract<PendingTableOp, { type: 'detach' | 'delete' }> =>
        op.type === 'detach' || op.type === 'delete'
      )
      .map((op) => op.tableId)
  );

  return positions
    .filter((p) => (p.tableId > 0 ? !removedIds.has(p.tableId) : tempIdToRealId.has(p.tableId)))
    .map((p) => (p.tableId > 0 ? p : { ...p, tableId: tempIdToRealId.get(p.tableId)! }));
}

/**
 * Ensures the floor being saved has a layout to persist positions into. If one is already active,
 * its id is returned unchanged. Otherwise a default layout is auto-created -- named after the
 * floor and sized to match it, isDefault=true -- so a floor's first save establishes a real
 * ManagedFloorLayout instead of continuing to write geometry straight onto managed_tables forever.
 */
export async function ensureLayoutId(
  activeLayout: Pick<FloorLayoutInfo, 'id'> | null,
  floor: Pick<FloorInfo, 'name' | 'width' | 'height'> | undefined,
  floorId: number,
  restaurantId: number,
  createLayout: (restaurantId: number, floorId: number, data: Partial<FloorLayoutInfo>) => Promise<FloorLayoutInfo>
): Promise<number> {
  if (activeLayout) return activeLayout.id;

  const created = await createLayout(restaurantId, floorId, {
    name: floor?.name || `Floor ${floorId}`,
    designWidth: floor?.width ?? 800,
    designHeight: floor?.height ?? 600,
    isDefault: true,
  });
  return created.id;
}
